/**
 * Preflight Health Check
 * Runs before React app loads to verify critical endpoints are available
 * Prevents infinite loops and 404 errors from breaking the app
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const GATEWAY_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api/v1', '')
  : '';

/**
 * Critical endpoints that MUST be available for the app to function
 * These are checked without authentication
 */
const CRITICAL_ENDPOINTS = [
  { path: '/monitor/health', name: 'Health Monitor' },
];

/**
 * Optional endpoints that enhance UX but aren't critical
 * These will show warnings if unavailable but won't block app load
 *
 * NOTE: Only check endpoints that:
 * 1. Are accessible without authentication
 * 2. Are needed during app initialization
 * 3. Would degrade UX if unavailable
 *
 * Auth-protected endpoints (/tenant/settings, /pipeline/*) are checked
 * after login via normal application flow - no need to preflight them.
 */
const OPTIONAL_ENDPOINTS = [
  { path: '/health', name: 'API Gateway', base: 'gateway' },
  // Electronic Invoice module v0.2 — non-blocking (feature flag tenant-scoped).
  // Renamed `invoice` → `e_invoice` to avoid Stripe billing collision.
  { path: '/api/v1/e_invoices?_limit=1', name: 'e_invoices', base: 'gateway' },
  // Vehicle catalog — standalone ~273 MB read-only SQLite, NOT shipped by git
  // or the gold master. Returns 503 when its file is absent on the server
  // (lost twice in prod). Non-blocking: only the model-picker dropdowns degrade,
  // but we want the boot warning so it's never a silent failure again.
  { path: '/api/v1/vehicle-catalog/stats', name: 'vehicle_catalog', base: 'gateway' },
];

/**
 * Check if an endpoint is available
 * @param {string} url - Full URL to check
 * @returns {Promise<{available: boolean, status: number, error?: string}>}
 */
async function checkEndpoint(url) {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000); // 3s timeout

    const response = await fetch(url, {
      method: 'HEAD', // Use HEAD to avoid fetching full response
      signal: controller.signal,
    });

    clearTimeout(timeout);

    return {
      // 5xx (e.g. the catalog's 503 when its DB file is absent) counts as
      // unavailable so OPTIONAL endpoints surface a boot warning instead of
      // looking healthy; a missing route (404) is also unavailable.
      available: response.status !== 404 && response.status < 500,
      status: response.status,
    };
  } catch (err) {
    if (err.name === 'AbortError') {
      return { available: false, status: 0, error: 'Timeout' };
    }
    return { available: false, status: 0, error: err.message };
  }
}

/**
 * Run preflight health checks
 * @returns {Promise<{ok: boolean, critical: Object[], optional: Object[]}>}
 */
export async function runPreflightChecks() {
  console.log('[Preflight] Starting health checks...');
  const startTime = Date.now();

  // Check critical endpoints
  const criticalResults = await Promise.all(
    CRITICAL_ENDPOINTS.map(async (endpoint) => {
      const result = await checkEndpoint(`${API_BASE}${endpoint.path}`);
      return { ...endpoint, ...result };
    })
  );

  // Check optional endpoints
  const optionalResults = await Promise.all(
    OPTIONAL_ENDPOINTS.map(async (endpoint) => {
      const base = endpoint.base === 'gateway' ? GATEWAY_BASE : API_BASE;
      const result = await checkEndpoint(`${base}${endpoint.path}`);
      return { ...endpoint, ...result };
    })
  );

  const duration = Date.now() - startTime;
  const allCriticalOk = criticalResults.every((r) => r.available);

  // Log results
  console.log(`[Preflight] Complete in ${duration}ms`);

  criticalResults.forEach((r) => {
    if (r.available) {
      console.log(`  ✅ ${r.name}: ${r.status}`);
    } else {
      console.error(`  ❌ ${r.name}: ${r.status || 'UNAVAILABLE'} ${r.error || ''}`);
    }
  });

  optionalResults.forEach((r) => {
    if (r.available) {
      console.log(`  ℹ️ ${r.name}: ${r.status}`);
    } else {
      console.warn(`  ⚠️ ${r.name}: ${r.status || 'UNAVAILABLE'} (non-critical)`);
    }
  });

  return {
    ok: allCriticalOk,
    critical: criticalResults,
    optional: optionalResults,
    duration,
  };
}

/**
 * Show error screen if critical endpoints are unavailable
 * @param {Object} results - Preflight check results
 */
export function showPreflightError(results) {
  const failedCritical = results.critical.filter((r) => !r.available);

  const errorHtml = `
    <div style="
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      font-family: system-ui, -apple-system, sans-serif;
      padding: 20px;
    ">
      <div style="
        max-width: 600px;
        background: white;
        border-radius: 12px;
        padding: 40px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      ">
        <h1 style="margin: 0 0 20px 0; color: #dc2626; font-size: 28px;">
          🚫 Service Unavailable
        </h1>

        <p style="margin: 0 0 20px 0; color: #4b5563; font-size: 16px; line-height: 1.6;">
          FleetManager cannot start because critical backend services are unavailable.
        </p>

        <div style="
          background: #fef2f2;
          border-left: 4px solid #dc2626;
          padding: 16px;
          margin-bottom: 20px;
          border-radius: 4px;
        ">
          <h2 style="margin: 0 0 12px 0; color: #991b1b; font-size: 16px; font-weight: 600;">
            Failed Health Checks:
          </h2>
          <ul style="margin: 0; padding-left: 20px; color: #7f1d1d;">
            ${failedCritical.map((r) => `
              <li style="margin-bottom: 8px;">
                <strong>${r.name}</strong>: ${r.error || 'Not responding'}
                <br><code style="font-size: 12px; color: #dc2626;">${API_BASE}${r.path}</code>
              </li>
            `).join('')}
          </ul>
        </div>

        <h3 style="margin: 20px 0 12px 0; color: #374151; font-size: 16px; font-weight: 600;">
          Troubleshooting:
        </h3>
        <ol style="margin: 0; padding-left: 20px; color: #6b7280; line-height: 1.8;">
          <li>Check if backend services are running:
            <br><code style="background: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-size: 13px;">netstat -ano | findstr ":9080 :9081"</code>
          </li>
          <li>Start services if not running:
            <br><code style="background: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-size: 13px;">start_all.bat</code>
          </li>
          <li>Check backend logs for errors</li>
        </ol>

        <button
          onclick="window.location.reload()"
          style="
            margin-top: 24px;
            width: 100%;
            padding: 12px 24px;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.2s;
          "
          onmouseover="this.style.background='#2563eb'"
          onmouseout="this.style.background='#3b82f6'"
        >
          🔄 Retry Connection
        </button>

        <p style="
          margin-top: 20px;
          text-align: center;
          color: #9ca3af;
          font-size: 13px;
        ">
          Check duration: ${results.duration}ms
        </p>
      </div>
    </div>
  `;

  document.body.innerHTML = errorHtml;
}

/**
 * Optional: Show warnings for non-critical endpoints
 * @param {Object} results - Preflight check results
 */
export function showOptionalWarnings(results) {
  const failedOptional = results.optional.filter((r) => !r.available);

  if (failedOptional.length > 0) {
    console.warn('[Preflight] Optional endpoints unavailable (non-critical):');
    failedOptional.forEach((r) => {
      console.warn(`  - ${r.name}: ${API_BASE}${r.path}`);
    });
  }
}

export default {
  runPreflightChecks,
  showPreflightError,
  showOptionalWarnings,
};
